/**
 * Sprint 4 Step 2 — Client helpers for domain telemetry and smart training.
 * PR-D4-064 — active drafts live in PostgreSQL, not localStorage.
 */

import type {
    ActiveProgressSession,
    ProgressDomainAccuracy,
    ProgressModuleType,
    ProgressSessionMode,
    ProgressSessionPayload,
    ProgressSummary,
    Question,
    TestResult,
} from './types';

export const DEFAULT_WEAKEST_MATH_DOMAIN = 'algebra';

export const MATH_DOMAIN_KEYS = ['algebra', 'advanced_math', 'psda', 'geometry'] as const;
export const VERBAL_DOMAIN_KEYS = [
    'craft_structure',
    'information_ideas',
    'standard_english_conventions',
    'expression_ideas',
] as const;

const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

export function normalizePracticeDomain(raw: string | null | undefined): string {
    const value = String(raw ?? '').trim().toLowerCase();
    if (!value) return DEFAULT_WEAKEST_MATH_DOMAIN;
    const head = value.split(/[/:.]/)[0] ?? value;
    if (head === 'problem_solving' || head === 'problem_solving_data_analysis') return 'psda';
    if ((MATH_DOMAIN_KEYS as readonly string[]).includes(head)) return head;
    if ((VERBAL_DOMAIN_KEYS as readonly string[]).includes(head)) return head;
    if (value.startsWith('algebra')) return 'algebra';
    if (value.startsWith('advanced')) return 'advanced_math';
    if (value.startsWith('geo')) return 'geometry';
    if (value.includes('craft')) return 'craft_structure';
    if (value.includes('information')) return 'information_ideas';
    if (value.includes('convention')) return 'standard_english_conventions';
    if (value.includes('expression')) return 'expression_ideas';
    return head || DEFAULT_WEAKEST_MATH_DOMAIN;
}

export function optionLetterFromIndex(index: number): string {
    return OPTION_LETTERS[index] ?? String(index);
}

export function domainsForModule(
    summary: ProgressSummary | null,
    module: 'math' | 'verbal',
): ProgressDomainAccuracy[] {
    if (!summary) return [];
    return summary.domains.filter((d) => d.module === module);
}

export function pickWeakestDomain(
    domains: ProgressDomainAccuracy[],
    fallback: string,
): string {
    const eligible = domains.filter((d) => d.attempts >= 1);
    if (eligible.length === 0) return fallback;
    return eligible.reduce((min, d) => (d.accuracy < min.accuracy ? d : min)).domain;
}

export function pickSmartMathDomain(summary: ProgressSummary | null): string {
    if (!summary) return DEFAULT_WEAKEST_MATH_DOMAIN;
    return pickWeakestDomain(domainsForModule(summary, 'math'), DEFAULT_WEAKEST_MATH_DOMAIN);
}

export function pickSmartVerbalDomain(summary: ProgressSummary | null): string | null {
    if (!summary) return null;
    const verbal = domainsForModule(summary, 'verbal');
    if (verbal.length === 0) return null;
    return pickWeakestDomain(verbal, verbal[0]!.domain);
}

export function questionDomain(question: Question, fallback: string): string {
    const raw =
        question.domain ||
        question.topic_id ||
        question.skill_id ||
        fallback;
    return normalizePracticeDomain(raw);
}

export function resumePathForSession(session: ActiveProgressSession): string {
    return session.moduleType === 'verbal' ? '/test?resume=1' : '/math-test?resume=1';
}

export function answersMapFromActiveSession(
    session: ActiveProgressSession,
): Map<number, number> {
    const order = session.questionsOrder.length
        ? session.questionsOrder
        : session.questions.map((question) => question.id);
    const optionByQuestionId = new Map(
        session.answers
            .filter((answer) => answer.selectedOption != null)
            .map((answer) => [answer.questionId, answer.selectedOption as number]),
    );
    const answers = new Map<number, number>();
    order.forEach((questionId, index) => {
        const selected = optionByQuestionId.get(questionId);
        if (selected != null) answers.set(index, selected);
    });
    return answers;
}

export function buildCompletedSessionPayload(args: {
    userId: string;
    moduleType: ProgressModuleType;
    mode: ProgressSessionMode;
    questions: Question[];
    answersByQuestionIndex?: ReadonlyMap<number, number>;
    results: TestResult[];
    fallbackDomain: string;
    startTime: number | null;
}): ProgressSessionPayload {
    const durationSeconds =
        args.startTime != null ? Math.max(0, Math.round((Date.now() - args.startTime) / 1000)) : 0;

    const resultByQuestionId = new Map(args.results.map((result) => [result.questionId, result]));
    const answers = args.questions.map((question, questionIndex) => {
        const result = resultByQuestionId.get(question.id);
        const selectedOption =
            args.answersByQuestionIndex?.get(questionIndex) ?? result?.selectedOption;
        const selected =
            selectedOption == null ? null : optionLetterFromIndex(selectedOption);
        return {
            questionId: question.id,
            domain: questionDomain(question, args.fallbackDomain),
            skill: question.skill_id ?? null,
            difficulty: question.difficulty_level || question.difficulty || null,
            selectedOption: selected,
            isCorrect: args.answersByQuestionIndex
                ? selectedOption != null && selectedOption === question.correct_option_index
                : result?.isCorrect ?? false,
            timeSpentSeconds: result?.timeSpentSeconds ?? 0,
            questionSnapshot: question,
        };
    });

    return {
        userId: args.userId,
        moduleType: args.moduleType,
        mode: args.mode,
        score: answers.filter((answer) => answer.isCorrect).length,
        maxScore: args.questions.length,
        durationSeconds,
        answers,
    };
}
